import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Button,
	Box,
	IconButton,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	DeleteStaffFilter,
	DeleteStaffListSummaryItem,
	StaffFilter,
	StaffListSummaryItem,
} from '@/api/DTO/staff/staffList.interface';
import StaffListSummaryFilterSection from './components/DeleteStaffRecordSummaryFilterSection';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import {
	useGetDeleteStaffPageQuery,
	useGetStaffPageQuery,
} from '@/api/staff/staffApiSlice';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import {
	DISPLAY_CHINESE_DATE_TIME_FORMAT,
	DISPLAY_DATETIME_12H_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteStaffRecordSummaryFilterSection from './components/DeleteStaffRecordSummaryFilterSection';

export interface deleteStaffListDataGrid {
	removedAt: string;
}

export default function DeleteStaffRecordSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);

	const defaultPropertyFilter: DeleteStaffFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<DeleteStaffFilter> = defaultPropertyFilter;
	const methods = useForm<DeleteStaffFilter>({
		defaultValues,
	});
	const transformedSearchParams: DeleteStaffFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetDeleteStaffPageQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: DeleteStaffListSummaryItem[]) => {
			if (response == null) return;
			const newResponse: deleteStaffListDataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					removedAt: !item.removedAt
						? '-'
						: dayjs(item.removedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as DeleteStaffFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: t('staff.staffName'),
			flex: 21,
		},
		{
			field: 'email',
			headerName: t('staff.email'),
			flex: 15,
		},
		{
			field: 'removedAt',
			headerName: t('staff.deleteTime'),
			flex: 21,
		},
		{
			field: 'removeStaffName',
			headerName: t('staff.takeStaff'),
			flex: 21,
		},
		// {
		// 	field: 'edit',
		// 	headerName: '',
		// 	width: 68,
		// 	renderCell: (params) => (
		// 		<Link
		// 			to={`/staff-manage/staff-list/info/${params.id}`}
		// 			state={{
		// 				propertySummarySearchParams: qs.stringify(transformedSearchParams),
		// 			}}
		// 		>
		// 			<IconButton color='primary'>
		// 				<VisibilityOutlinedIcon />
		// 			</IconButton>
		// 		</Link>
		// 	),
		// },
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.deleteStaffRecord')}
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<DeleteStaffRecordSummaryFilterSection></DeleteStaffRecordSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
